import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Head from 'next/head';
import { CurrentItemQuery } from '../../../graphql/graphql';
import {
  BuildingTypeMap,
  SCHEMA_CONTEXT,
  SchemaOrgType
} from '../../../seo.constants';

interface CurrentItemPageSchemaProps {
  item: CurrentItemQuery['item'];
  isPublished: boolean;
  name: string | ReactNode;
}

export const CurrentItemPageSchema: React.FC<CurrentItemPageSchemaProps> = ({
  item,
  isPublished,
  name
}) => {
  const { t } = useTranslation();

  const area = useMemo(() => {
    const units = item?.area?.units || '';
    const value = item?.area?.value;
    return ['sot', 'сот'].includes(units) ? value * 100 : value;
  }, [item?.area]);

  const itemOffered = useMemo(
    () => ({
      '@type': BuildingTypeMap[item?.category?.id] || SchemaOrgType.Place,
      ...(item?.rooms && { numberOfRooms: item.rooms }),
      address: {
        '@type': SchemaOrgType.PostalAddress,
        addressLocality: item?.city?.name,
        addressRegion: t('pages.current_item.country'),
        streetAddress: item?.address
      },
      floorSize: {
        '@type': SchemaOrgType.QuantitativeValue,
        value: area,
        unitCode: 'MTK'
      }
    }),
    [area, item?.rooms, item?.city?.name, item?.address, t]
  );

  const offers = useMemo(
    () => ({
      '@type': SchemaOrgType.Offer,
      price: `${item?.price?.value}`,
      priceCurrency: item?.price?.currency,
      availability: `https://schema.org/${isPublished ? 'InStock' : 'SoldOut'}`,
      itemOffered
    }),
    [item?.price, isPublished, itemOffered]
  );

  const itemJsonLd = useMemo(
    () => ({
      '@context': SCHEMA_CONTEXT,
      '@type': SchemaOrgType.Product,
      name,
      image: (item?.photos || []).map(photo => photo.full),
      description: item?.description,
      category: 'Real Estate',
      offers
    }),
    [name, item?.photos, item?.description, offers]
  );

  return (
    <Head>
      <script
        type="application/ld+json"
        data-cy="current-item-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemJsonLd) }}
      />
    </Head>
  );
};
